/**
 * Creates an isolated queue namespace with sequential operations per scope/key.
 * Scopes are weakly held; settled keys are removed. Failures release the next
 * explicit operation and never trigger retries.
 * @returns A stable enqueue function; create it outside component renders.
 */
export const createKeyedAsyncQueue = () => {
  const queues = new WeakMap<object, Map<string, Promise<void>>>();

  return <T>(
    scope: object,
    key: string,
    operation: () => Promise<T>
  ): Promise<T> => {
    const pending = queues.get(scope) ?? new Map<string, Promise<void>>();
    queues.set(scope, pending);
    const result = (pending.get(key) ?? Promise.resolve()).then(operation);
    const settled = result.then(
      () => undefined,
      () => undefined
    );
    pending.set(key, settled);
    void settled.then(() => {
      if (pending.get(key) === settled) pending.delete(key);
      if (pending.size === 0) queues.delete(scope);
    });
    return result;
  };
};
